import { useMemo, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { ChangeTodaySheet } from "../components/ChangeTodaySheet";
import { Hero } from "../components/Hero";
import { I, SESSION_ICON } from "../components/icons";
import { Ring } from "../components/Ring";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { Stat, StatStrip } from "../components/StatStrip";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { DOW_LABELS, dayLabel, dayMonth, parseDay, todayStr } from "../domain/date";
import { doseIndex, sessionFor, sessionKeyFor, typeName } from "../domain/plan";
import { programmeWeekNo, ruleLine } from "../domain/programme";
import { retestDue } from "../domain/fitnessTests";
import { blockWhere, sessionWhere, steadyFigures, steadyWeekRows } from "../domain/programmeView";
import { buildQueue, queueMinutes, sessionPct } from "../domain/queue";
import { SESSION_LABEL } from "../domain/session";
import { sessionProgress } from "../domain/week";
import { useDeviceAnswers, useKaya, usePlayerMode, useTrainingDevice } from "../store/kayaStore";
import { sessionGate } from "../domain/trainingQuestions";
import { usePlayerIntent } from "../store/playerIntent";
import { backAfterBreak, backLine } from "../domain/readiness";
import { completedWeek } from "../domain/weekDone";
import { WeekTicks } from "../components/WeekTicks";
import { YogaExtraRow } from "./yogaExtra";
import "./Train.css";

/**
 * Train for a person on the Steady programme (board TrainSteady, ADR-052): today's template with
 * its real length and the plan's one rule line, the week in plain figures — no streak, no red for
 * a day not done — the template's blocks, the week, Plan my exercise, and the short version for a
 * bad day.
 */
export default function TrainSteady() {
  const S = useKaya((s) => s.S);
  const session = useKaya((s) => s.session);
  const history = useHistory();
  const intent = usePlayerIntent();
  const device = useTrainingDevice();
  const answers = useDeviceAnswers();
  const pm = usePlayerMode();
  const [changing, setChanging] = useState(false);

  const today = todayStr();
  // the safety answers on today's session: held → the Chair session, 180/110 → none (P9, sessionGate)
  const gate = sessionGate(S, sessionKeyFor(S, today), answers);
  const key = gate.key;
  const filled = useMemo(() => sessionFor(key, S, { device }), [key, S, device]);
  const queue = useMemo(() => buildQueue(S, key, doseIndex(S, key, today), { device }), [S, key, device]);
  const short = useMemo(() => buildQueue(S, key, doseIndex(S, key, today), { device, short: true }), [S, key, device]);
  const mins = queueMinutes(S, queue, pm.simple);
  const moves = queue.filter((q) => q.kind !== "rest").length;
  const pct = sessionPct(S, today, key) ?? 0;
  const prog = sessionProgress(S, today, key);
  const rows = steadyWeekRows(S, today, pm.simple);
  const fig = steadyFigures(S, today, pm.simple);
  const due = retestDue(S, today);
  const setBy = S.profile.training?.setBy;
  const rest = !filled.blocks.length;
  const resumeLabel = session ? `Resume ${SESSION_LABEL[session.kind].toLowerCase()}` : null;
  // the completed week (ADR-066); after more than a week away the Short version is the session,
  // and it counts (ADR-065) — until the day is begun, when the frozen day decides
  const week = completedWeek(S, today, pm.simple);
  const back = backAfterBreak(S, today);
  const firstBack = back !== null && !rest && !gate.stop && pct === 0 && short.length > 0;
  const shortMins = queueMinutes(S, short, pm.simple);

  const player = (route: string) => history.push(route, { from: history.location.pathname });
  const start = () => (firstBack ? startShort() : player(session ? session.route : "/workout"));
  const startShort = () => {
    if (session) return player(session.route);
    intent.setShort();
    player("/workout");
  };

  const primary = gate.stop
    ? { label: "Start", onClick: () => undefined, icon: I.play, disabled: true, reason: "A lower BP reading, under Plan my exercise, brings it back" }
    : rest
    ? { label: resumeLabel ?? "Start a walk", onClick: () => player(session ? session.route : "/walk"), icon: I.walk }
    : {
        label:
          session && session.kind !== "workout"
            ? (resumeLabel ?? "Resume")
            : pct >= 1
              ? "Done — view"
              : pct > 0 && prog
                ? `Continue · ${prog.done} of ${prog.total}`
                : `Start · ${firstBack ? shortMins : mins} min`,
        onClick: start,
        icon: pct >= 1 ? I.check : I.play,
      };

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="train-page">
            <TopBar title="Train" sub={`Steady · week ${programmeWeekNo(S, today)}`} />

            <Hero
              label={
                <>
                  Steady programme · {dayLabel(today)}
                  {setBy ? (
                    <>
                      {" "}
                      <Tag tone="neutral">set by {setBy}</Tag>
                    </>
                  ) : null}
                </>
              }
              title={gate.stop ? "No session today" : rest ? "Rest day" : typeName(key)}
              note={gate.line ?? undefined}
              line={
                gate.stop
                  ? undefined
                  : rest
                  ? `${week.line} so far`
                  : firstBack && back !== null
                    ? backLine(back, true)
                    : prog && pct > 0
                    ? `${prog.done} of ${prog.total} moves done · ${mins} min`
                    : `${mins} min · ${moves} moves${sessionWhere(filled.blocks) ? ` · ${sessionWhere(filled.blocks)}` : ""}`
              }
              sub={rest && !gate.stop ? "A rest day — an easy walk, or the yoga and breathing session below." : (ruleLine(S, today) ?? undefined)}
              visual={
                !rest && pct > 0 ? (
                  <Ring pct={pct} size={72} stroke={7} color="var(--brand)" figure={prog ? `${prog.done}/${prog.total}` : null} />
                ) : undefined
              }
              visualSide
              onPress={() => history.push(rest ? "/train/week" : "/train/session")}
              ariaLabel={`Today: ${rest ? "rest day" : `${typeName(key)}, ${mins} minutes`}. Show the moves`}
            />

            <WeekTicks week={week} />

            <StatStrip ariaLabel="This week in figures">
              <Stat figure={`${fig.strengthDone} of ${fig.strengthOf}`} label="Strength days" note="this week" to="/train/week" />
              <Stat figure={fig.walkMin} label="Walk min" note="this week" to="/train/walks" />
              <Stat
                figure={fig.chairStand ? fig.chairStand.n : null}
                label="Chair stands"
                note={fig.chairStand ? dayLabel(fig.chairStand.ds) : "no test yet"}
                to="/progress/training"
              />
            </StatStrip>

            {rest || gate.stop ? null : (
              <RowGroup heading={<>Today’s session<span className="train-hsub">{mins} min</span></>}>
                {filled.blocks.map((b, bi) => (
                  <NavRow
                    key={`${b.title}:${bi}`}
                    title={b.title}
                    subtitle={`${b.items.length} move${b.items.length === 1 ? "" : "s"} · ${blockWhere(b, bi === filled.blocks.length - 1)}`}
                    icon={I[SESSION_ICON[key]]}
                    to="/train/session"
                  />
                ))}
              </RowGroup>
            )}

            <RowGroup heading={<>This week<span className="train-hsub">{fig.strengthDone} of {fig.strengthOf} strength days</span></>}>
              {rows.map((r) => (
                <NavRow
                  key={r.ds}
                  title={`${DOW_LABELS[parseDay(r.ds).getDay()]} · ${r.name}`}
                  subtitle={r.sub ?? undefined}
                  icon={
                    r.done ? (
                      I.check
                    ) : r.rest ? (
                      I.minus
                    ) : (
                      <span className="train-ring">
                        <Ring pct={r.pct} size={22} stroke={3} color="var(--brand)" />
                      </span>
                    )
                  }
                  tone={r.done ? "success" : "default"}
                  value={r.minutes !== null ? `${r.minutes} min` : undefined}
                  to={r.isToday && !r.rest ? "/train/session" : undefined}
                />
              ))}
            </RowGroup>

            <YogaExtraRow group="Any day" />

            <RowGroup heading="Plan my exercise">
              <NavRow
                title="Answers"
                subtitle={due ? `Retest due ${dayMonth(due)}` : "Change what the plan is built from"}
                icon={I.list}
                to="/train/questions"
              />
              <NavRow title="Your week" subtitle="Strength never two days running" icon={I.calendar} to="/train/week" />
            </RowGroup>

            {rest || gate.stop || !short.length ? null : (
              <RowGroup heading="Short version">
                {firstBack ? (
                  <NavRow title="Short version" subtitle="Today's session is already the Short version" icon={I.clock} />
                ) : (
                  <NavRow
                    title="Short version"
                    subtitle={`${shortMins} minutes · for a bad day; it counts as done`}
                    icon={I.clock}
                    onClick={startShort}
                  />
                )}
              </RowGroup>
            )}

            <ActionBar primary={primary} secondary={{ label: "Change", onClick: () => setChanging(true) }} />
          </div>
        </Screen>
        <ChangeTodaySheet open={changing} onClose={() => setChanging(false)} ds={today} />
      </IonContent>
    </IonPage>
  );
}
